/* eslint-disable react/prop-types */
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Icon } from "@dialogueconsulting/sked-ui";
import { colors } from "styles/color-tokens";
import { useDispatch, useSelector } from "react-redux";
import { setAccountsCounts } from "actions/skedCore";
import { openSocialAccountConnectDialog } from "features/social-account-connect";
import { UpsellOrderSummary } from "./upsell-order-summary";
import { User } from "features/user";
import { getAccountsCount } from "api/accounts";
import Bugsnag from "@bugsnag/js";
import {
  PlanCurrency,
  PlanEstimateResponse,
  PlanPeriodUnit,
  PlanTypeV2,
} from "./types";
import QuotaSuccess from "assets/icons/upsell/quota-success.svg";
import { fetchPlans, useUserSubscriptionAccountCount } from "./plan-api";
import { getUser } from "selectors/commonSelectors";
import { closeUpsellModal } from "actions/typed-modal";
import { openSupportChat } from "libs/support";
import { getPlanEstimateV2 } from "api/plan-change";
import { useDebouncedCallback } from "use-debounce";
import { renderPriceWithSymbol } from "./common-components";
import { useTrackEvent } from "../../libs/analytics";
import { PricingVersionAlerts } from "./pricing-version-alerts";
import { Alert, AlertType } from "../../ui/alert";
import { toast } from "react-toastify";
import { Button } from "@/components/ui/button";

// SKE-3462: empirical Chargebee `subscription_entitlement` propagation
// window after a successful `update_for_items`. The CB live-fetch path
// can keep returning the pre-upgrade quota for several seconds; this is
// long enough to cover the typical case without making the success step
// feel sluggish.
const CB_PROPAGATION_RETRY_MS = 2000;

export default function IncreaseAccountQuota() {
  const dispatch = useDispatch();
  const [totalAccounts, setTotalAccounts] = useState(0);
  const [step, setStep] = useState<"selection" | "confirmation" | "success">(
    "selection"
  );
  const initialAccounts = totalAccounts;
  const [estimatedPrice, setEstimatedPrice] = useState(0);
  const [fetchingEstimateLoading, setFetchingEstimateLoading] = useState(false);
  const [userPlanData, setUserPlanData] = useState<PlanTypeV2>();
  const currentAccountCount = useUserSubscriptionAccountCount();
  const remainingQuota = currentAccountCount ?? 0;
  const trackEvent = useTrackEvent();

  const user = useSelector(getUser) as User;
  const [planLabel, setPlanLabel] = useState(user.planLabel);
  const { subscription } = user.subscription;

  useEffect(() => {
    setTotalAccounts(currentAccountCount ?? 0);

    trackEvent({
      eventName: "account-quota-viewed",
      useServices: ["AMP", "HS"],
    });

    fetchPlans({
      adminId: user.adminId,
      currency: subscription.currency_code as PlanCurrency,
      numberAccounts: currentAccountCount ?? 0,
    }).then((data: any) => {
      const planId =
        subscription.billing_period_unit === "year"
          ? subscription.plan_id + "-annual"
          : subscription.plan_id;

      const userPlan = data.find((d: any) => d.id === planId);
      if (!userPlan) {
        return alert(
          "Sorry, your plan is custom or we are unable to generate an estimate. Please open live chat and our team will assist with your plan changes."
        );
      }
      // plan title is the description of this object
      setPlanLabel(userPlan.name);
      setEstimatedPrice(userPlan.price);
      setUserPlanData(userPlan);
    });
  }, []);

  const debouncedGetEstimate = useDebouncedCallback(() => {
    getPlanEstimateV2({
      plan: subscription.plan_id,
      numberAccounts: totalAccounts,
      currency: subscription.currency_code as PlanCurrency,
      billingPeriod: subscription.billing_period_unit as PlanPeriodUnit,
    })
      .then((data: PlanEstimateResponse) => {
        console.log("ge", data);
        const nextInvoiceEstimate =
          data.data.planEstimate.estimate.next_invoice_estimate;
        // @ts-ignore
        setEstimatedPrice(
          (nextInvoiceEstimate?.line_items ?? []).reduce(
            (accum: any, item: any) => (accum += item.amount),
            0
          )
        );

        setFetchingEstimateLoading(false);
      })
      .catch((error) => {
        toast.error(
          error && error.error
            ? error.error
            : "Sorry, we can't get an estimate for your account change. Please contact support and we can assist."
        );
        setFetchingEstimateLoading(false);
      });
  }, 500);

  useEffect(() => {
    if (totalAccounts - remainingQuota > 0) {
      setFetchingEstimateLoading(true);

      debouncedGetEstimate();
    }
  }, [totalAccounts]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    if (!/^\d*$/.test(newValue)) {
      return;
    }

    setTotalAccounts(+newValue);
  };

  if (step === "confirmation") {
    return (
      <UpsellOrderSummary
        context={{
          currency: subscription.currency_code as PlanCurrency,
          periodUnit: subscription.billing_period_unit as PlanPeriodUnit,
          numberOfAccounts: totalAccounts,
          planId: subscription.plan_id,
          planName: planLabel,
          extraAccountPrice: userPlanData?.additionalAccountsPrice || 0,
          planAccountLimit: userPlanData?.planAccountLimit,
          isDowngrade: initialAccounts > totalAccounts,
        }}
        onBack={() => {
          setStep("selection");
        }}
        onSuccess={async () => {
          // SKE-3462: optimistic patch left totalCount stale and never
          // corrected drift between client and CB entitlement. Pull a fresh
          // /accounts/count so both maxAllowed and totalCount land in sync.
          // If the first read still shows the pre-upgrade quota, do one more
          // attempt after a brief delay — covers the typical CB
          // subscription_entitlement propagation window after update_for_items.
          // After that we trust the server-side subscription_items cross-check
          // (also in this PR) to keep the next add-account call honest.
          try {
            let counts = await getAccountsCount();
            if (
              typeof counts.maxAllowed === "number" &&
              counts.maxAllowed < totalAccounts
            ) {
              await new Promise((resolve) =>
                setTimeout(resolve, CB_PROPAGATION_RETRY_MS)
              );
              counts = await getAccountsCount();
              if (
                typeof counts.maxAllowed === "number" &&
                counts.maxAllowed < totalAccounts
              ) {
                // Beyond the typical CB propagation window — likely a real
                // outage or stuck entitlement. The server-side
                // subscription_items cross-check (canAddAccount in
                // schedugram-server, same ticket) keeps the next add-account
                // call working, but flag this so we can spot pattern shifts
                // in the propagation window.
                Bugsnag.notify(
                  new Error(
                    `increase-account-quota: CB entitlement still stale after ${CB_PROPAGATION_RETRY_MS}ms (got ${counts.maxAllowed}, expected ${totalAccounts})`
                  )
                );
              }
            }
            // setAccountsCounts already writes maxAllowed (which may
            // legitimately be `null` for unlimited tiers). Calling
            // updateMaxAllowed afterwards would overwrite that with a
            // numeric default, so we skip it on the success path.
            dispatch(setAccountsCounts(counts));
          } catch (error) {
            // Don't block the success step on a transient counts fetch
            // failure — fall back to the optimistic value so the user
            // can still proceed. Promise rejections aren't always Error
            // instances; normalise so Bugsnag gets a useful event.
            const normalizedError =
              error instanceof Error ? error : new Error(String(error));
            Bugsnag.notify(normalizedError, (event) => {
              if (!(error instanceof Error)) {
                event.addMetadata("originalError", { value: String(error) });
              }
            });
            dispatch(
              setAccountsCounts({
                maxAllowed: totalAccounts,
              })
            );
          }
          setStep("success");
        }}
      />
    );
  }

  if (step === "success") {
    return <IncreaseQuotaSuccess increasedAccountsNumber={totalAccounts} />;
  }

  return (
    <div>
      <Heading>Change account quota</Heading>
      <SubHeading>
        Add on accounts to your plan over the base included accounts. Your
        current plan is <strong>{user.planLabel}</strong>.
      </SubHeading>
      <SubHeading
        style={{
          marginBottom: "16px",
        }}
      >
        You currently have a quota of <span>{remainingQuota}</span> social
        accounts. Select the overall number of accounts you need to change your
        quota.
      </SubHeading>
      <Content>
        <AlertAccountsContainer>
          <PricingVersionAlerts
            pricingVersion={user.pricingVersion}
            planLabel={user.planLabel}
          />

          <div className="total-accounts">
            How many <span>total</span> accounts do you want to manage?
            <div className="input-wrap">
              <input
                type="text"
                value={totalAccounts}
                onChange={handleInputChange}
              />
              <div className="controller">
                <Icon
                  name="chevron-up"
                  size="sm"
                  onClick={() => {
                    setTotalAccounts((prev) => prev + 1);
                  }}
                />
                <Icon
                  name="chevron-down"
                  size="sm"
                  onClick={() => {
                    setTotalAccounts((prev) => Math.max(0, prev - 1));
                  }}
                />
              </div>
            </div>
            {userPlanData?.planAccountLimit !== undefined &&
              totalAccounts > userPlanData.planAccountLimit && (
                <div style={{ marginTop: "20px" }}>
                  <Alert
                    type={AlertType.error}
                    title="You have too many accounts for this plan"
                  >
                    On the {planLabel} plan you can only have up to{" "}
                    {userPlanData?.planAccountLimit} accounts. Upgrade your plan
                    to enterprise or contact support for more options.
                  </Alert>
                </div>
              )}
            {totalAccounts > 20 && (
              <div style={{ marginTop: "20px" }}>
                <Alert
                  type={AlertType.info}
                  title="You might better suit a custom plan"
                >
                  Speak to your success manager or our support team about plan
                  changes as you may be better suited to a custom plan.
                </Alert>
              </div>
            )}
          </div>
        </AlertAccountsContainer>
        <PlanBox>
          <p className="plan-name">{planLabel}</p>
          <p className="price">
            {renderPriceWithSymbol({
              currency: subscription.currency_code,
              price: (estimatedPrice / 100).toLocaleString(),
              periodUnit: subscription.billing_period_unit,
            })}
            <span className="term">
              /
              {subscription.billing_period_unit === "year"
                ? "yearly"
                : "monthly"}
            </span>
          </p>
          <p className="number-of-accounts">
            For <span className="count">{totalAccounts}</span> accounts
          </p>
          <div className="btnWrap">
            <Button
              disabled={fetchingEstimateLoading}
              variant="blue-primary"
              size="md"
              onClick={() => {
                setStep("confirmation");
              }}
            >
              <i className="fa-regular fa-at"></i>
              Change account quota
            </Button>
          </div>
        </PlanBox>
      </Content>
      <Footer>
        <div className="any-question">
          <p>Any questions? </p>
          <p
            onClick={() => {
              openSupportChat("", "plans");
            }}
            className="contact-support"
          >
            Please <span>contact Support</span>
            <Icon name="heart-hand" size="sm" color={colors.primary500} />
          </p>
        </div>
        <a
          className="full-pricing"
          href="https://skedsocial.com/pricing"
          target="_blank"
          rel="noreferrer"
        >
          Full pricing
          <Icon name="share-03" size="sm" color={colors.primary500} />
        </a>
      </Footer>
    </div>
  );
}

const Heading = styled.div`
  color: ${colors.primary900};
  font-family: "Gotham SSm A", "Gotham SSm B", Helvetica, Arial, sans-serif;
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: 32px;
  padding-bottom: 4px;
`;

const SubHeading = styled.p`
  color: ${colors.neutral700};
  font-family: Inter, Helvetica, Arial, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  margin: 0;

  span {
    color: ${colors.primary500};
    font-weight: 600;
  }
`;
const AlertAccountsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  margin-right: 1em;
  width: 100%;
`;
const Content = styled.div`
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .total-accounts {
    font-family: Inter, Helvetica, Arial, sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px;

    .input-wrap {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-top: 16px;

      input {
        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }

        &:focus {
          outline: none;
        }

        width: 40px;
        padding: 8px;
        border-radius: 8px;
        border: 1px solid ${colors.neutral100};
        background: ${colors.white};
      }

      .controller {
        svg {
          cursor: pointer;
        }
      }
    }

    .reduce-quota {
      color: ${colors.neutral500};
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 20px;
      padding-top: 16px;
    }

    span {
      color: ${colors.primary500};
    }
  }
`;

const PlanBox = styled.div`
  border-radius: 8px;
  border: 1px solid ${colors.primary500};
  width: 400px;
  padding: 20px 16px;
  text-align: center;
  user-select: none;

  .plan-name {
    color: ${colors.neutral800};
    font-family: Inter, Helvetica, Arial, sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px;
    margin: 0;
    text-transform: capitalize;
  }

  .price {
    color: ${colors.neutral800};
    font-family: Inter, Helvetica, Arial, sans-serif;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 4px 0 0 0;

    .currency {
      font-size: 12px;
      font-weight: 600;
    }

    .price {
      font-family: "Gotham SSm A", "Gotham SSm B", Helvetica, Arial, sans-serif;
      font-size: 24px;
      font-style: normal;
      font-weight: 600;
      line-height: 32px;
    }

    .term {
      margin-left: 4px;
      font-size: 12px;
      font-style: normal;
      font-weight: 600;
      line-height: 18px;
    }
  }

  .number-of-accounts {
    font-family: Inter, Helvetica, Arial, sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    margin: 16px 0 0;

    .count {
      color: ${colors.primary500};
      font-weight: 600;
    }
  }

  .accounts-desc {
    color: ${colors.neutral500};
    text-align: center;
    font-family: Inter, Helvetica, Arial, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    margin-top: 0;
    margin-bottom: 16px;
  }

  .btnWrap {
    display: flex;
    justify-content: center;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  color: ${colors.neutral500};
  margin-top: 16px;
  font-family: Inter, Helvetica, Arial, sans-serif;
  font-size: 14px;
  user-select: none;

  .any-question {
    p {
      margin: 0;
      font-size: 14px;
      font-weight: 400;
      color: ${colors.neutral500};
      width: 400px;
    }
  }

  a {
    color: ${colors.primary500};
    text-decoration: none;
  }

  .full-pricing,
  .contact-support {
    cursor: pointer;
    color: ${colors.primary500};
    display: flex;
    align-items: center;
    gap: 4px;
    font-weight: 500;
    margin-left: 4px;

    > span {
      font-weight: 500;
      color: ${colors.primary500};
    }
  }
`;

const IncreaseQuotaSuccess: React.FC<{
  increasedAccountsNumber: number;
}> = ({ increasedAccountsNumber }) => {
  const dispatch = useDispatch();

  return (
    <Container>
      <QuotaSuccess />
      <h2>
        Woohoo, your account quota is now increased to {increasedAccountsNumber}
        !
      </h2>
      <span className="start-linking">
        Start linking your social accounts now.
      </span>
      <AddAccountButtonWrap>
        <Button
          variant="blue-primary"
          size="md"
          onClick={() => {
            openSocialAccountConnectDialog();
            dispatch(closeUpsellModal());
          }}
        >
          <i className="fa-regular fa-plus"></i>
          Add Account
        </Button>
      </AddAccountButtonWrap>
    </Container>
  );
};

const Container = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  h2 {
    color: ${colors.primary900};
    text-align: center;
    font-family: "Gotham SSm A", "Gotham SSm B", sans-serif;
    font-size: 24px;
    font-style: normal;
    font-weight: 500;
    line-height: 32px;
    margin-bottom: 8px;
  }

  .start-linking {
    color: ${colors.neutral800};
    text-align: center;
    font-family: Inter, Helvetica, Arial, sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
  }
`;

const AddAccountButtonWrap = styled.div`
  margin: auto;
  margin-top: 24px;
`;
